import type { CampaignRegime } from '../../campaign/campaign.types';
import type { DealPhase } from '../../feed/deal.types';
import type { SecurityClass } from '../../feed/feed.types';
import { BRAND_NAME } from '../../shared/brand';
import { SYMBOL } from './format';
import {
  closedDateOf,
  compactMoney,
  count,
  daysBetween,
  fullMoney,
  parts,
  scaleOf,
  stated,
} from './raise-figures';

/**
 * The raise page's "About this raise" (views/raise.hbs, under the stats
 * card): a few short paragraphs written from the raise's stored figures, so
 * a page whose source gives one line of text still says what the raise is.
 * Pure: no model writes it at render time, and the page's script imports
 * `aboutDate` to redraw its dates in the reader's zone.
 *
 * - **Only what is stated.** A sentence is written only from figures the
 *   source states; a missing or zero figure leaves its sentence (or its
 *   clause) out, never "0" or a guess.
 * - **Not every page alike.** Each sentence has several wordings, and the
 *   slug picks one per sentence (`pick`), so a page always reads the same
 *   while two raises seldom read alike.
 * - **120 to 200 words.** The figures come first. Past MAX_WORDS the least
 *   telling sentences go (`keep`, lowest first); short of MIN_WORDS, plain
 *   context about the regulation, the instrument, the filing and where the
 *   figures came from is added, in that order, while it fits. A record that
 *   states almost nothing stays short rather than padded with guesses.
 * - **Compared with similar raises** where at least MIN_SIMILAR others on
 *   the platform, under the same sector, opened in the last 12 months
 *   (SimilarRaisesService reads them).
 */

export const MIN_WORDS = 120;
export const MAX_WORDS = 200;

/** Fewer similar raises than this and there is no comparison. */
export const MIN_SIMILAR = 5;

/** What the block reads about the raise: RaiseFacts' fields it uses. */
export interface AboutFacts {
  slug: string;
  name: string;
  tagline: string | null;
  platformId: string | null;
  platformName: string;
  /** Where the platform is based, as a name: residents-only raises are open to it. */
  platformCountry: string | null;
  filing: boolean;
  /** The SEC form behind an EDGAR row: "C", "C/A". */
  filingForm: string | null;
  regime: CampaignRegime;
  instrument: string | null;
  securityClass: SecurityClass | null;
  converts: boolean;
  sector: string | null;
  /** The issuer's country, as a name. */
  country: string | null;
  currency: string | null;
  raised: number | null;
  target: number | null;
  max: number | null;
  investors: number | null;
  minInvestment: number | null;
  valuation: number | null;
  /** true: open beyond the platform's home market; false: its residents only. */
  openToForeign: boolean | null;
  launchAt: Date | null;
  closeAt: Date | null;
  /** When it ended, where that is known (endedAtOf). */
  endedAt?: Date | null;
  checkedAt: Date;
  phase: DealPhase;
}

/** Other raises like this one: same platform, same sector, last 12 months. */
export interface SimilarRaises {
  /** How many, this raise left out. */
  raises: number;
  /** Their median target, in the raise's currency; null where too few state one in it. */
  medianTarget: number | null;
  /** Their median investor count; null where too few state one. */
  medianInvestors: number | null;
}

/** SimilarRaisesService's row: each median with how many figures it was taken over. */
export interface SimilarRow {
  raises: number;
  targets: number;
  medianTarget: number | null;
  investorCounts: number;
  medianInvestors: number | null;
}

/**
 * The comparison the block may make, or null: none with fewer than
 * MIN_SIMILAR similar raises, and no median taken over fewer than
 * MIN_SIMILAR stated figures, so one or two raises never stand for a group.
 */
export function similarOf(row: SimilarRow | null): SimilarRaises | null {
  if (!row || row.raises < MIN_SIMILAR) return null;
  const median = (n: number, value: number | null) =>
    n >= MIN_SIMILAR && value !== null && Number.isFinite(value) && value > 0
      ? value
      : null;
  const medianTarget = median(row.targets, row.medianTarget);
  const medianInvestors = median(row.investorCounts, row.medianInvestors);
  if (medianTarget === null && medianInvestors === null) return null;
  return { raises: row.raises, medianTarget, medianInvestors };
}

/** A run of the block's text: plain, or a date the page's script redraws. */
export type Segment =
  { text: string; date?: undefined } | { text: string; date: string };

export interface AboutView {
  heading: string;
  /** Each paragraph's runs, to be printed back to back. */
  paragraphs: Segment[][];
  words: number;
}

/** "22 October 2026": the block's dates, in the reader's zone. */
export function aboutDate(date: Date, zone: string): string {
  const p = parts(date, zone, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `${p.day} ${p.month} ${p.year}`;
}

// ---- Picking a wording ------------------------------------------------------------

/** FNV-1a, 32 bits: the same slug always gives the same number. */
export function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * One of a sentence's wordings, by the slug and the sentence's own key, so
 * each sentence picks independently of the others.
 */
export function pick<T>(slug: string, key: string, variants: readonly T[]): T {
  return variants[hash(`${slug}\u0000${key}`) % variants.length];
}

// ---- Sentences --------------------------------------------------------------------

type Part = string | Date;

/** A sentence's text with its dates kept as dates, for the script to redraw. */
function t(strings: TemplateStringsArray, ...values: Part[]): Part[] {
  const out: Part[] = [];
  strings.forEach((text, i) => {
    if (text) out.push(text);
    if (i < values.length) out.push(values[i]);
  });
  return out;
}

interface Sentence {
  /**
   * Which paragraph: the raise, its terms, its dates and who may invest,
   * the comparison, then any context.
   */
  para: 0 | 1 | 2 | 3 | 4;
  /** Its place in the paragraph. */
  order: number;
  /** How long it stays when the block runs long: the lowest goes first. */
  keep: number;
  parts: Part[];
}

/**
 * "£975,117" below a million, then "£6.2M" rounded down (compactMoney), with
 * the symbols the site's cards and the title print, so "$" and not en-GB's
 * "US$" whatever the size.
 */
export function proseMoney(amount: number, currency: string | null): string {
  if (amount >= 1_000_000) return compactMoney(amount, currency);
  const symbol = SYMBOL[currency ?? 'USD'];
  return symbol
    ? `${symbol}${count(Math.round(amount))}`
    : fullMoney(amount, currency);
}

const plural = (n: number, one: string, many: string) =>
  `${count(n)} ${n === 1 ? one : many}`;

/** "Loopwell’s", "Seedrs’". */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}’` : `${name}’s`;
}

/** "the United Kingdom", "Germany". */
export function withArticle(country: string): string {
  return /^(United |Netherlands$|Philippines$|Bahamas$|Gambia$|Maldives$|Seychelles$|Isle of |Czech Republic$|Dominican Republic$|Central African Republic$|.+ Islands$)/.test(
    country,
  )
    ? `the ${country}`
    : country;
}

/** The regulation, named in full the first time: null for OTHER. */
export const REGIME_IN_FULL: Record<CampaignRegime, string | null> = {
  REG_CF: 'Regulation Crowdfunding (Reg CF)',
  REG_A: 'Regulation A+ (Reg A+)',
  ECSPR: 'the EU’s European Crowdfunding Service Providers Regulation (ECSPR)',
  FCA: 'UK rules overseen by the Financial Conduct Authority (FCA)',
  CSF: 'Australia’s crowd-sourced funding (CSF) regime',
  NI_45_110:
    'Canada’s start-up crowdfunding exemption, National Instrument 45-110',
  OTHER: null,
};

/**
 * What a regulation is, for a block short of MIN_WORDS: what it allows and
 * who it lets invest, never a limit or a figure, which would need checking
 * against the law as it stands.
 */
const REGIME_CONTEXT: Record<Exclude<CampaignRegime, 'OTHER'>, string[]> = {
  REG_CF: [
    'Reg CF lets a US company raise from the general public, not only accredited investors, through an SEC-registered funding portal or broker-dealer.',
    'Under Reg CF, a US company can raise from anyone, not just accredited investors, as long as the offering runs through an SEC-registered intermediary.',
    'Reg CF is the SEC’s route for raising from the general public through a registered intermediary, with no need for investors to be accredited.',
  ],
  REG_A: [
    'Reg A+ is the SEC’s exemption for larger offerings to the general public, which the SEC has to qualify before any securities are sold.',
    'Under Reg A+, an offering can take money from the general public once the SEC has qualified it.',
    'A Reg A+ offering is open to the general public, but only after the SEC has qualified its offering statement.',
  ],
  ECSPR: [
    'ECSPR sets one set of rules for licensed crowdfunding platforms across the EU, including what a raise has to disclose to investors.',
    'Under ECSPR, each raise comes with a key investment information sheet (KIIS) setting out its main terms and risks.',
    'ECSPR is the EU-wide regulation for crowdfunding platforms, and it requires a key investment information sheet for every raise.',
  ],
  FCA: [
    'In the UK, a platform that arranges investments has to be authorised by the FCA, or act for a firm that is.',
    'UK investment crowdfunding is regulated by the FCA, which sets rules on how platforms present raises and check that investors understand the risks.',
    'The FCA regulates UK investment crowdfunding, including the checks a platform runs before a new investor can put money in.',
  ],
  CSF: [
    'Australia’s CSF rules let eligible companies raise from the general public through a licensed intermediary.',
    'Under the CSF regime, retail investors invest through a licensed CSF intermediary and have a cooling-off period after each investment.',
    'The CSF regime is Australia’s route for raising from retail investors, through an intermediary licensed by ASIC.',
  ],
  NI_45_110: [
    'NI 45-110 lets an early-stage Canadian company raise from the public through a funding portal, with limits on how much each investor can put in.',
    'Under NI 45-110, a start-up can raise from the public in Canada through a funding portal, within caps on the raise and on each investment.',
    'NI 45-110 is Canada’s start-up crowdfunding exemption, which caps both what the company can raise and what each investor can put in.',
  ],
};

/** A second line on Reg CF, for a filing that states little else. */
const REG_CF_LIMITS = [
  'Under Reg CF, how much a non-accredited investor can put in over 12 months depends on their income and net worth.',
  'Reg CF limits how much a non-accredited investor can put in over 12 months, by their income and net worth; accredited investors have no such limit.',
];

/** What investors are offered: "preferred shares", "a SAFE (…)". */
function offered(facts: AboutFacts): string | null {
  switch (facts.instrument) {
    case 'SAFE':
      return 'a SAFE (simple agreement for future equity)';
    case 'Convertible':
      return 'a convertible security';
    case 'ASA':
      return 'an advance subscription agreement (ASA)';
    case 'Equity':
      return facts.securityClass === 'Preferred'
        ? 'preferred shares'
        : 'shares';
    case 'Revenue share':
      return 'a revenue share';
    case 'Bond / loan':
      return 'debt';
    default:
      return null;
  }
}

/** What the instrument is, for a block short of MIN_WORDS. */
function instrumentContext(facts: AboutFacts): string[] | null {
  const name = facts.name;
  switch (facts.instrument) {
    case 'SAFE':
      return [
        'A SAFE is not a share today: it converts into shares at a later priced round, on the terms it sets.',
        'A SAFE gives investors the right to shares at a future priced round, rather than shares now.',
        'With a SAFE, investors receive shares later, usually when the company raises a priced round.',
      ];
    case 'Convertible':
      return [
        'A convertible security turns into shares later, usually at the company’s next priced round.',
        'Convertible securities convert into shares at a later date, usually when the company next raises a priced round.',
      ];
    case 'ASA':
      return [
        'With an advance subscription agreement, investors pay now for shares the company issues at a later round, or by a long-stop date.',
        'An ASA pays now for shares the company issues later, at its next round or by a set long-stop date.',
      ];
    case 'Equity':
      return [
        `Shares make investors part-owners of ${name}.`,
        `With shares, investors own a part of ${name}.`,
        `Buying shares makes investors part-owners of ${name}.`,
      ];
    case 'Revenue share':
      return [
        'A revenue share pays investors a part of the company’s revenue, rather than giving them ownership.',
        'With a revenue share, investors are paid from the company’s revenue instead of owning part of it.',
      ];
    case 'Bond / loan':
      return [
        'Debt means investors lend to the company on set terms rather than buy a part of it.',
        'With debt, investors lend money to the company on agreed terms instead of buying shares.',
      ];
    default:
      return null;
  }
}

/** The intermediary an EDGAR row's tagline names: "Offered through Fundify Portal, LLC". */
function intermediaryOf(facts: AboutFacts): string | null {
  const match = facts.tagline
    ?.replace(/\s+/g, ' ')
    .trim()
    .match(/^offered through (.+)$/i);
  return match ? match[1] : null;
}

/** The sentence with its full stop, unless it ends on one already ("…, Inc."). */
function stop(sentence: Part[]): Part[] {
  const last = sentence[sentence.length - 1];
  return typeof last === 'string' && /[.!?]$/.test(last)
    ? sentence
    : [...sentence, '.'];
}

/**
 * Everything the block could say about the raise: its figures, each in the
 * wording the slug picks, and the context that may follow them.
 */
function sentencesOf(
  facts: AboutFacts,
  similar: SimilarRaises | null,
  now: Date,
  zone: string,
): { figures: Sentence[]; context: Sentence[] } {
  const slug = facts.slug;
  const choose = (key: string, variants: Part[][]) => pick(slug, key, variants);
  const name = facts.name;
  const platform = facts.platformName;
  const nameOf = possessive(name);
  const platformOf = possessive(platform);
  const cash = (amount: number) => proseMoney(amount, facts.currency);

  const upcoming = facts.phase === 'UPCOMING';
  const closed = facts.phase === 'CLOSED';
  const gone = facts.phase === 'WITHDRAWN' || facts.phase === 'DELISTED';
  const ended = closed || gone;
  const round = facts.filing ? 'offering' : 'round';
  // As the page's title calls it (pageTitle in site/raise.ts).
  const kind =
    facts.instrument === 'Bond / loan'
      ? 'crowdfunding raise'
      : 'equity crowdfunding raise';
  const aKind = `${/^[aeiou]/.test(kind) ? 'an' : 'a'} ${kind}`;

  const figures: Sentence[] = [];
  const context: Sentence[] = [];
  const add = (
    list: Sentence[],
    para: Sentence['para'],
    order: number,
    keep: number,
    sentence: Part[] | null,
  ) => {
    if (sentence) list.push({ para, order, keep, parts: sentence });
  };

  // -- The raise: what, where, under which rules ------------------------------------

  const launched = facts.launchAt;
  if (facts.filing) {
    const form =
      facts.filingForm === 'C/A' ? 'an amended Form C (Form C/A)' : 'a Form C';
    const through = intermediaryOf(facts);
    const opening = stop(
      launched
        ? choose('open', [
            t`${name} filed ${form} with the SEC on ${launched}${through ? `, offering its securities through ${through}` : ''}`,
            t`On ${launched}, ${name} filed ${form} with the US Securities and Exchange Commission${through ? ` for an offering through ${through}` : ''}`,
            t`${name} has filed ${form} with the SEC, dated ${launched}${through ? `; the offering runs through ${through}` : ''}`,
          ])
        : choose('open', [
            t`${name} has filed ${form} with the SEC${through ? `, offering its securities through ${through}` : ''}`,
            t`${name} filed ${form} with the US Securities and Exchange Commission${through ? ` for an offering through ${through}` : ''}`,
          ]),
    );
    add(figures, 0, 0, 100, opening);
  } else {
    const opening = upcoming
      ? choose('open', [
          t`${name} has ${aKind} coming up on ${platform}.`,
          t`${name} is preparing to open ${aKind} on ${platform}.`,
          t`${platform} lists ${name} as an upcoming ${kind}.`,
        ])
      : closed
        ? choose('open', [
            t`${name} ran ${aKind} on ${platform}, which has now closed.`,
            t`${nameOf} ${kind} on ${platform} has closed.`,
            t`The ${kind} ${name} ran on ${platform} has closed.`,
          ])
        : facts.phase === 'WITHDRAWN'
          ? choose('open', [
              t`${nameOf} ${kind} on ${platform} was withdrawn.`,
              t`${platform} shows ${nameOf} ${kind} as withdrawn.`,
            ])
          : facts.phase === 'DELISTED'
            ? choose('open', [
                t`${nameOf} ${kind} is no longer listed on ${platform}.`,
                t`${name} had ${aKind} on ${platform}, which no longer lists it.`,
              ])
            : choose('open', [
                t`${name} is raising money on ${platform}.`,
                t`${name} is running ${aKind} on ${platform}.`,
                t`${platform} is hosting ${aKind} for ${name}.`,
                t`${name} has an open ${kind} on ${platform}.`,
              ]);
    add(figures, 0, 0, 100, opening);
  }

  // A platform's own tag; a filing states none. "Other" says nothing.
  const sector =
    !facts.filing && facts.sector && !/^\s*others?\s*$/i.test(facts.sector)
      ? facts.sector
      : null;
  const home = facts.country ? withArticle(facts.country) : null;
  const where =
    sector && home
      ? choose('where', [
          t`The company is based in ${home}, and ${platform} lists it under ${sector}.`,
          t`Based in ${home}, ${name} sits in ${platformOf} ${sector} category.`,
          t`${platform} files ${name} under ${sector}; the company is based in ${home}.`,
        ])
      : sector
        ? choose('where', [
            t`${platform} lists it under ${sector}.`,
            t`On ${platform}, it sits in the ${sector} category.`,
            t`${platform} files the raise under ${sector}.`,
          ])
        : home
          ? choose('where', [
              t`The company is based in ${home}.`,
              t`${name} is based in ${home}.`,
              t`It is a company based in ${home}.`,
            ])
          : null;
  add(figures, 0, 1, 60, where);

  const regime = REGIME_IN_FULL[facts.regime];
  const security = offered(facts);
  const terms =
    regime && security
      ? choose('terms', [
          t`Investors are offered ${security} under ${regime}.`,
          t`Under ${regime}, investors are offered ${security}.`,
          t`The ${round} is for ${security}, made under ${regime}.`,
        ])
      : regime
        ? choose('terms', [
            t`The ${round} is made under ${regime}.`,
            t`It is offered under ${regime}.`,
          ])
        : security
          ? choose('terms', [
              t`Investors are offered ${security}.`,
              t`The ${round} is for ${security}.`,
            ])
          : null;
  add(figures, 0, 2, 70, terms);

  // -- Its terms and its progress ---------------------------------------------------

  if (stated(facts.valuation)) {
    const v = cash(facts.valuation);
    const short =
      facts.instrument === 'SAFE' || facts.instrument === 'ASA'
        ? facts.instrument
        : 'convertible';
    const valuation = facts.converts
      ? choose('valuation', [
          t`The ${short} carries a valuation cap of ${v}.`,
          t`Its valuation cap is ${v}.`,
          t`The cap on the valuation at which it converts is ${v}.`,
        ])
      : choose('valuation', [
          t`The pre-money valuation is ${v}.`,
          t`${name} is valued at ${v} before the new money.`,
          t`The ${round} values ${name} at ${v} pre-money.`,
        ]);
    add(figures, 1, 0, 50, valuation);
  }

  // The maximum only where it is above the target, as the page's bar measures it.
  const target = stated(facts.target) ? cash(facts.target) : null;
  const max =
    stated(facts.max) && !(stated(facts.target) && facts.max <= facts.target)
      ? cash(facts.max)
      : null;
  const goal =
    facts.filing && (target || max)
      ? target && max
        ? choose('goal', [
            t`The Form C sets a target of ${target} and a maximum of ${max}.`,
            t`The filing states a target of ${target} and a maximum of ${max}.`,
            t`In the filing, the target is ${target} and the maximum ${max}.`,
          ])
        : target
          ? choose('goal', [
              t`The Form C sets a target of ${target}.`,
              t`The filing states a target of ${target}.`,
            ])
          : choose('goal', [
              t`The Form C sets a maximum of ${max!}.`,
              t`The filing states a maximum of ${max!}.`,
            ])
      : target && max
        ? choose('goal', [
            t`The target is ${target}, with a maximum of ${max}.`,
            t`${name} set a target of ${target} and a maximum of ${max}.`,
            t`Its target is ${target}, and its maximum ${max}.`,
          ])
        : target
          ? choose('goal', [
              t`The target is ${target}.`,
              t`${name} set a target of ${target}.`,
              t`Its target is ${target}.`,
            ])
          : max
            ? choose('goal', [
                t`The maximum is ${max}.`,
                t`${name} set a maximum of ${max}.`,
                t`The ${round} is capped at ${max}.`,
              ])
            : null;
  add(figures, 1, 1, 80, goal);

  const raised = stated(facts.raised) ? cash(facts.raised) : null;
  const backers =
    facts.investors !== null && facts.investors > 0
      ? plural(facts.investors, 'investor', 'investors')
      : null;
  const have = facts.investors === 1 ? 'has' : 'have';
  // Reservations fill no offering, so an upcoming raise has no share funded.
  const scale = scaleOf(facts.target, facts.max);
  const pct =
    !upcoming && stated(facts.raised) && scale
      ? Math.floor((facts.raised / scale.amount) * 100)
      : 0;
  const share = pct > 0 ? `${count(pct)}% of its ${scale!.basis}` : null;
  const comma = share ? `, ${share}` : '';
  const brackets = share ? ` (${share})` : '';
  const progress = upcoming
    ? raised && backers
      ? choose('raised', [
          t`Ahead of launch, ${backers} ${have} reserved ${raised}.`,
          t`${raised} has been reserved by ${backers} before it opens.`,
        ])
      : raised
        ? choose('raised', [
            t`Ahead of launch, ${raised} has been reserved.`,
            t`${raised} has been reserved before it opens.`,
          ])
        : null
    : closed
      ? raised && backers
        ? choose('raised', [
            t`It raised ${raised} from ${backers}${comma}.`,
            t`${backers} put in ${raised}${brackets}.`,
            t`The ${round} closed with ${raised} from ${backers}${comma}.`,
          ])
        : raised
          ? choose('raised', [
              t`It raised ${raised}${comma}.`,
              t`The final reported total was ${raised}${comma}.`,
            ])
          : backers
            ? choose('raised', [
                t`${backers} took part.`,
                t`It drew ${backers}.`,
              ])
            : null
      : gone
        ? raised && backers
          ? choose('raised', [
              t`When last reported, it had raised ${raised} from ${backers}${comma}.`,
              t`Its last reported figures were ${raised} from ${backers}${brackets}.`,
            ])
          : raised
            ? choose('raised', [
                t`When last reported, it had raised ${raised}${comma}.`,
                t`Its last reported total was ${raised}${brackets}.`,
              ])
            : backers
              ? choose('raised', [t`When last reported, it had ${backers}.`])
              : null
        : raised && backers
          ? choose('raised', [
              t`So far it has raised ${raised} from ${backers}${comma}.`,
              t`${backers} ${have} put in ${raised} so far${brackets}.`,
              t`It has raised ${raised} from ${backers} so far${share ? `, which is ${share}` : ''}.`,
            ])
          : raised
            ? choose('raised', [
                t`So far it has raised ${raised}${comma}.`,
                t`${raised} has been raised so far${brackets}.`,
                t`It stands at ${raised} raised${comma}.`,
              ])
            : backers
              ? choose('raised', [
                  t`${backers} ${have} backed it so far.`,
                  t`So far ${backers} ${have} put money in.`,
                  t`It has ${backers} so far.`,
                ])
              : null;
  add(figures, 1, 2, 90, progress);

  if (stated(facts.minInvestment)) {
    const min = cash(facts.minInvestment);
    const minimum = ended
      ? choose('minimum', [
          t`The minimum investment was ${min}.`,
          t`Tickets started at ${min}.`,
          t`Investors could put in as little as ${min}.`,
        ])
      : choose('minimum', [
          t`The minimum investment is ${min}.`,
          t`Investors can put in as little as ${min}.`,
          t`Tickets start at ${min}.`,
        ]);
    add(figures, 1, 3, 40, minimum);
  }

  // -- Its dates, and who may invest ------------------------------------------------

  const close = facts.closeAt;
  if (facts.filing) {
    // With the target the filing states, as the filing's terms.
    const deadline = close
      ? close > now
        ? choose('dates', [
            t`The offering deadline in the filing is ${close}.`,
            t`Its offering deadline is ${close}.`,
            t`Under the filing, the offering runs until ${close}.`,
          ])
        : choose('dates', [
            t`The offering deadline in the filing was ${close}.`,
            t`Its offering deadline was ${close}.`,
          ])
      : null;
    add(figures, 1, 4, 75, deadline);
  } else {
    // A launch date ahead is an opening; behind, the day it opened.
    const opened = launched && launched <= now ? launched : null;
    const opens = launched && launched > now ? launched : null;
    // A raise can close early, so a closed one's date is when it closed.
    const ends = closed ? closedDateOf(facts, now) : close;
    const days = opened && ends ? daysBetween(opened, ends, zone) : 0;
    const run = days > 1 ? `, a run of ${count(days)} days` : '';
    const dates = upcoming
      ? // Before launch a close date can be the end of a pre-launch phase
        // (a Birchal expression of interest), so only the opening is said.
        opens
        ? choose('dates', [
            t`It is due to open on ${opens}.`,
            t`It opens on ${opens}.`,
            t`The raise is set to open on ${opens}.`,
          ])
        : null
      : closed
        ? opened && ends
          ? choose('dates', [
              t`It ran from ${opened} to ${ends}${run}.`,
              t`It opened on ${opened} and closed on ${ends}.`,
              t`The raise was open from ${opened} until ${ends}.`,
            ])
          : opened
            ? choose('dates', [
                t`It opened on ${opened}.`,
                t`It went live on ${opened}.`,
              ])
            : ends
              ? choose('dates', [
                  t`It closed on ${ends}.`,
                  t`Its close date was ${ends}.`,
                ])
              : null
        : facts.phase === 'WITHDRAWN'
          ? opened
            ? choose('dates', [
                t`It had opened on ${opened}.`,
                t`It went live on ${opened}.`,
              ])
            : null
          : facts.phase === 'DELISTED'
            ? opened && ends
              ? choose('dates', [
                  t`It opened on ${opened} and was due to close on ${ends}.`,
                  t`It went live on ${opened}, with a close date of ${ends}.`,
                ])
              : opened
                ? choose('dates', [t`It opened on ${opened}.`])
                : ends
                  ? choose('dates', [t`It was due to close on ${ends}.`])
                  : null
            : opened && ends
              ? choose('dates', [
                  t`It opened on ${opened} and closes on ${ends}${run}.`,
                  t`The raise has been open since ${opened} and is due to close on ${ends}.`,
                  t`It runs from ${opened} to ${ends}${run}.`,
                ])
              : opened
                ? choose('dates', [
                    t`It opened on ${opened}.`,
                    t`It has been open since ${opened}.`,
                    t`The raise went live on ${opened}.`,
                  ])
                : ends
                  ? choose('dates', [
                      t`It is due to close on ${ends}.`,
                      t`It closes on ${ends}.`,
                      t`The close date is ${ends}.`,
                    ])
                  : null;
    add(figures, 2, 0, 75, dates);
  }

  if (facts.openToForeign !== null && !facts.filing) {
    const base = facts.platformCountry
      ? withArticle(facts.platformCountry)
      : null;
    const can = ended ? 'could' : 'can';
    const is = ended ? 'was' : 'is';
    const eligible = facts.openToForeign
      ? base
        ? choose('eligible', [
            t`Investors outside ${base} ${can} take part as well, subject to ${platformOf} own checks.`,
            t`It ${is} open to investors outside ${base}, within ${platformOf} eligibility rules.`,
            t`Investors from outside ${base} ${can} invest too, subject to ${platformOf} checks.`,
          ])
        : choose('eligible', [
            t`It ${is} open to investors from other countries, subject to ${platformOf} own checks.`,
            t`Investors from abroad ${can} take part, within ${platformOf} eligibility rules.`,
          ])
      : base
        ? choose('eligible', [
            t`Only residents of ${base} ${can} invest.`,
            t`It ${is} open to residents of ${base} only.`,
            t`${platform} ${ended ? 'took' : 'takes'} investors resident in ${base} only for this raise.`,
          ])
        : choose('eligible', [
            t`Only residents of ${platformOf} home country ${can} invest.`,
            t`It ${is} open to local residents only.`,
          ]);
    add(figures, 2, 1, 55, eligible);
  }

  // -- Compared with similar raises -------------------------------------------------

  if (similar && sector && facts.platformId) {
    const k = count(similar.raises);
    const mt =
      similar.medianTarget !== null ? cash(similar.medianTarget) : null;
    const mi =
      similar.medianInvestors !== null
        ? count(Math.round(similar.medianInvestors))
        : null;
    const medians =
      mt && mi
        ? choose('similar', [
            t`Over the last 12 months, ${BRAND_NAME} has tracked ${k} other raises on ${platform} listed under ${sector}: their median target was ${mt}, and their median investor count ${mi}.`,
            t`Among the ${k} other ${platform} raises listed under ${sector} in the last 12 months, the median target was ${mt} and the median investor count was ${mi}.`,
            t`For comparison, the ${k} other raises in ${platformOf} ${sector} category over the past 12 months had a median target of ${mt} and a median of ${mi} investors.`,
          ])
        : mt
          ? choose('similar', [
              t`Over the last 12 months, ${BRAND_NAME} has tracked ${k} other raises on ${platform} listed under ${sector}, with a median target of ${mt}.`,
              t`Among the ${k} other ${platform} raises listed under ${sector} in the last 12 months, the median target was ${mt}.`,
              t`For comparison, the ${k} other raises in ${platformOf} ${sector} category over the past 12 months had a median target of ${mt}.`,
            ])
          : choose('similar', [
              t`Over the last 12 months, ${BRAND_NAME} has tracked ${k} other raises on ${platform} listed under ${sector}, with a median of ${mi!} investors each.`,
              t`Among the ${k} other ${platform} raises listed under ${sector} in the last 12 months, the median investor count was ${mi!}.`,
              t`For comparison, the ${k} other raises in ${platformOf} ${sector} category over the past 12 months drew a median of ${mi!} investors.`,
            ]);
    add(figures, 3, 0, 65, medians);

    // Within a tenth either way is "in line": a median is no exact line.
    const side = (own: number, median: number) =>
      own > median * 1.1 ? 'above' : own < median * 0.9 ? 'below' : 'level';
    const tSide =
      mt && stated(facts.target) && similar.medianTarget
        ? side(facts.target, similar.medianTarget)
        : null;
    // Reservations before launch are not investors yet.
    const iSide =
      mi && !upcoming && backers && similar.medianInvestors
        ? side(facts.investors!, similar.medianInvestors)
        : null;
    const tWords = { above: 'above', below: 'below', level: 'in line with' };
    const iWords = {
      above: 'more investors than',
      below: 'fewer investors than',
      level: 'about as many investors as',
    };
    const n = count(facts.investors ?? 0);
    const soFar = ended ? '' : ' so far';
    const has = ended ? 'had' : 'has';
    const versus =
      tSide && iSide
        ? choose('versus', [
            t`${nameOf} target of ${target!} is ${tWords[tSide]} that median, and with ${backers!}${soFar} it ${has} ${iWords[iSide]} the median raise.`,
            t`At ${target!}, its target is ${tWords[tSide]} the median; its ${backers!}${soFar} put it ${tWords[iSide]} the median investor count.`,
            t`Its ${target!} target sits ${tWords[tSide]} the median, and its investor count${soFar}, ${n}, is ${tWords[iSide]} the median.`,
          ])
        : tSide
          ? choose('versus', [
              t`${nameOf} target of ${target!} is ${tWords[tSide]} that median.`,
              t`At ${target!}, its target is ${tWords[tSide]} the median.`,
              t`Its ${target!} target sits ${tWords[tSide]} the median.`,
            ])
          : iSide
            ? choose('versus', [
                t`With ${backers!}${soFar}, ${name} ${has} ${iWords[iSide]} the median raise.`,
                t`Its ${backers!}${soFar} put it ${tWords[iSide]} the median investor count.`,
              ])
            : null;
    add(figures, 3, 1, 45, versus);
  }

  // -- Context, for a block short of MIN_WORDS --------------------------------------

  // Added in this order while the block is short; read in `order`'s.
  if (facts.regime !== 'OTHER') {
    add(context, 4, 1, 0, [pick(slug, 'regime', REGIME_CONTEXT[facts.regime])]);
  }
  const instrument = instrumentContext(facts);
  if (instrument) add(context, 4, 3, 0, [pick(slug, 'instrument', instrument)]);
  if (facts.filing) {
    add(context, 4, 0, 0, [
      pick(
        slug,
        'form',
        facts.filingForm === 'C/A'
          ? [
              'A Form C/A amends an earlier Form C, for example to change the offering’s terms or its deadline.',
              'An amended Form C replaces details of an earlier filing, such as the offering’s terms or deadline.',
            ]
          : [
              'A Form C is the disclosure a company files with the SEC before raising under Reg CF; it sets out the offering’s terms, the intermediary and the company’s financial statements.',
              'A Form C sets out an offering’s terms, the intermediary that runs it and the company’s financial statements, and is filed with the SEC before a Reg CF raise.',
            ],
      ),
    ]);
  }
  const anyFigure =
    [
      facts.raised,
      facts.target,
      facts.max,
      facts.minInvestment,
      facts.valuation,
    ].some(stated) || backers !== null;
  if (anyFigure) {
    const checked = facts.checkedAt;
    const provenance = facts.filing
      ? choose('provenance', [
          t`These terms come from the filing on SEC EDGAR.`,
          t`Every figure here is taken from the filing on SEC EDGAR.`,
        ])
      : choose('provenance', [
          t`These figures are ${platformOf} own, as ${BRAND_NAME} last read them on ${checked}.`,
          t`${BRAND_NAME} last read these figures from ${platform} on ${checked}.`,
          t`The figures here come from ${platform}, as of ${possessive(BRAND_NAME)} last check on ${checked}.`,
        ]);
    add(context, 4, 4, 0, provenance);
  }
  if (facts.regime === 'REG_CF') {
    add(context, 4, 2, 0, [pick(slug, 'limits', REG_CF_LIMITS)]);
  }
  if (!ended) {
    const through = facts.filing ? intermediaryOf(facts) : platform;
    if (through) {
      const whereTo = choose('where-to', [
        t`Investments are made through ${through}; ${BRAND_NAME} doesn’t take money or hold shares.`,
        t`Money goes through ${through}, not through ${BRAND_NAME}, which doesn’t handle investments.`,
      ]);
      add(context, 4, 5, 0, whereTo);
    }
  }

  return { figures, context };
}

// ---- The block --------------------------------------------------------------------

function textOf(sentence: Sentence, zone: string): string {
  return sentence.parts
    .map((p) => (typeof p === 'string' ? p : aboutDate(p, zone)))
    .join('');
}

const wordsIn = (text: string) => text.split(/\s+/).filter(Boolean).length;

/** "About this raise", from the facts the page loaded and its similar raises. */
export function aboutRaiseView(
  facts: AboutFacts,
  similar: SimilarRaises | null,
  now: Date,
  zone: string,
): AboutView {
  const { figures, context } = sentencesOf(facts, similar, now, zone);
  const words = (list: Sentence[]) =>
    list.reduce((sum, s) => sum + wordsIn(textOf(s, zone)), 0);

  const kept = [...figures];
  while (words(kept) > MAX_WORDS && kept.length > 1) {
    const least = Math.min(...kept.map((s) => s.keep));
    kept.splice(
      kept.findIndex((s) => s.keep === least),
      1,
    );
  }
  for (const line of context) {
    if (words(kept) >= MIN_WORDS) break;
    if (words([...kept, line]) <= MAX_WORDS) kept.push(line);
  }

  kept.sort((a, b) => a.para - b.para || a.order - b.order);
  const paragraphs: Segment[][] = [];
  let para = -1;
  for (const sentence of kept) {
    if (sentence.para !== para) {
      paragraphs.push([]);
      para = sentence.para;
    }
    const runs = paragraphs[paragraphs.length - 1];
    if (runs.length > 0) runs.push({ text: ' ' });
    for (const p of sentence.parts) {
      runs.push(
        typeof p === 'string'
          ? { text: p }
          : { text: aboutDate(p, zone), date: p.toISOString() },
      );
    }
  }
  return {
    heading: 'About this raise',
    paragraphs: paragraphs.map(merge),
    words: words(kept),
  };
}

/** Neighbouring plain runs as one, so the template prints fewer pieces. */
function merge(runs: Segment[]): Segment[] {
  const out: Segment[] = [];
  for (const run of runs) {
    const last = out[out.length - 1];
    if (last && last.date === undefined && run.date === undefined) {
      out[out.length - 1] = { text: last.text + run.text };
    } else {
      out.push(run);
    }
  }
  return out;
}

/** The block's text, paragraphs apart: for tests and for reading it back. */
export function aboutText(view: AboutView): string {
  return view.paragraphs
    .map((runs) => runs.map((r) => r.text).join(''))
    .join('\n\n');
}
